<script setup lang="ts">
import { useCacheStore } from "@/store/useCacheStore";
import { useDocketStore } from "@/utils/DocketStore";
import { formatPrice } from "@/utils/Formatting";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import debounce from "lodash/debounce";


// Interface
interface Emit {
  (e: "closeAddToMyTabDialog", value?: boolean): void;
  (e: "addToMyTab", value?: any): void;
}

interface Props {
  isDialogVisible: boolean;
  user?:string,
  docketId?:string
}

// Props
const props = defineProps<Props>();

// Emits
const emit = defineEmits<Emit>();

// Store
const docketStore = useDocketStore();
const userStore = useUserStore();
const { currentLocation } = storeToRefs(userStore);
const cacheStore = useCacheStore();
const { updateCacheMemory } = cacheStore;

// 👉 headers
const headers = [
  // { title: "#", key: "id" },
  { title: "Created date", key: "created", sortable: true, width: "5%" },
  { title: "Docket", key: "docketNumber", sortable: true, width: "10%" },
  { title: "TAB Name", key: "description", sortable: true },
  { title: "User", key: "user", sortable: true, width: "10%" },
  { title: "Total", key: "total", sortable: true, width: "5%" },
  { title: "Due", key: "due", sortable: true, width: "5%" },
  { title: "Limit", key: "limit", sortable: true, width: "5%" },
] as any[];

const options = ref<any>({
  page: 1,
  itemsPerPage: 10,
  sortBy: [],
  groupBy: [],
  search: undefined,
});

// Reactive data
const dockets = ref<any>([]);
const totalDockets = ref<number>(0);
const isLoading = ref<boolean>(false);
const selectedDocket = ref<string | undefined>();

// Computed to get the total and due amount from docket
const getLineTotal = computed(() => (docket: any) => {
  let total = 0;
  let amountDue = 0;
  let paymentsTotal = 0;

  const docketLinesItems = docket.expand?.docketLines_via_docket || [];
  const docketPaymentsItems = docket.expand?.docketPayments_via_docket || [];

  // Use a single reduce to calculate creditSurchargeAmount, tipAmount, and paymentsTotal from docketPaymentsItems
  paymentsTotal = docketPaymentsItems.reduce(
    (accumulator: number, currentValue: any) => {
      if (currentValue.status === "Approved") {
        const amount = currentValue.amount ?? 0;

        return accumulator + amount;
      }

      return accumulator;
    },
    0
  );

  // Use a single reduce to calculate total discount and docket total from docketLinesItems
  const docketTotal = docketLinesItems.reduce(
    (accumulator: number, currentValue: any) => {
      const lineTotal = currentValue.lineTotal ?? 0;

      return accumulator + lineTotal;
    },
    0
  );

  // Calculate the final total and amount due
  total = docketTotal;
  amountDue = docketTotal - paymentsTotal;

  return {
    total: docketTotal,
    due: amountDue,
  };
});

// Fetch dockets from docket store
const fetchDockets = async () => {
  isLoading.value = true;

  const response = await docketStore.fetchDockets({
    status: ["Open", "Partial"],
    location: currentLocation.value,
    page: options.value.page,
    itemsPerPage: options.value.itemsPerPage,
    sortBy: options.value.sortBy,
    user: props?.user && props?.user != '' ? props?.user : false
  });

  if (response) {
    dockets.value = response.items.filter((docket: any) => docket.id != props?.docketId);
    totalDockets.value = response.totalItems || 0;
    options.value.page = response.page;
  }

  isLoading.value = false;
};

// Debounce the fetchDockets function
const debouncedFetchDockets = debounce(async () => {
  await fetchDockets();
}, 500);

// Watch for changes in the options
watch(options, async (newValue, oldValue) => {
  await debouncedFetchDockets();
});

// Watch for changes in the dialog visibility
watch(
  () => props.isDialogVisible,
  async (newValue, oldValue) => {
    if (!newValue) selectedDocket.value = undefined;
  }
);
</script>

<template>
  <div>
    <VDialog
      :model-value="props.isDialogVisible"
      persistent
      max-width="max-content"
      max-height="800"
      no-click-animation
      @update:model-value="emit('closeAddToMyTabDialog', false)"
    >
      <VCard>
        <div
          class="d-flex align-center justify-space-between bg-dialogHeaderColor"
        >
          <VCardTitle> Add To a Tab </VCardTitle>

          <VBtn
            icon="mdi-close"
            size="large"
            variant="text"
            class="rounded-0"
            color=""
            @click="emit('closeAddToMyTabDialog', false)"
          />
        </div>

        <!-- Docket table -->
        <VCardText>
          <VDataTableServer
            v-model:items-per-page="options.itemsPerPage"
            v-model:page="options.page"
            :loading="isLoading"
            :items-length="totalDockets"
            :headers="headers"
            :items="dockets"
            sticky
            class="text-no-wrap rounded-0"
            @update:options="
              ($event: any) => {
                options = $event;
                if (!isLoading) {
                  debouncedFetchDockets();
                }
              }
            "
          >
            <!-- created -->
            <template #item.created="{ item }">
              <span class="text-sm">
                {{
                  item.created
                    ? moment(item.created).format("DD-MM h:mm a")
                    : ""
                }}
              </span>
            </template>

            <!-- docketNumber -->
            <template #item.docketNumber="{ item }">
              <VBtn
                icon="mdi-plus"
                :variant="selectedDocket === item.id ? 'flat' : 'outlined'"
                class="rounded-sm pa-2"
                block
                @click="selectedDocket = item.id"
              >
                #{{ item.docketNumber }}
              </VBtn>
            </template>

            <!-- User -->
            <template #item.user="{ item }">
              <div class="d-flex flex-column" v-if="item?.expand?.user">
                <span class="text-sm text-decoration-none">
                  <RouterLink
                    :to="`user/${item.user}`"
                    class="font-weight-medium"
                  >
                    {{ item?.expand?.user?.firstName }}
                    {{ item?.expand?.user?.lastName }}
                  </RouterLink>
                </span>

                <span class="text-caption"
                  >@{{ item?.expand?.user?.username }}</span
                >
              </div>
            </template>

            <!-- Total -->
            <template #item.total="{ item }">
              <span class="text-sm">
                {{ formatPrice(getLineTotal(item)?.total) }}
              </span>
            </template>

            <!-- Due -->
            <template #item.due="{ item }">
              <span class="text-sm">
                {{ formatPrice(getLineTotal(item)?.due) }}
              </span>
            </template>

            <!-- Limit -->
            <template #item.limit="{ item }">
              <span v-if="item?.limit > 0" class="text-sm">
                {{ formatPrice(item?.limit) }}
              </span>
            </template>

            <!-- pagination  -->
            <template #bottom>
              <VDivider />
              <div
                class="d-flex gap-x-6 flex-wrap justify-end pa-2 align-center"
              >
                <div class="text-sm ms-2">{{ totalDockets }} Records</div>
                <VSpacer />
                <div class="d-flex align-center gap-x-2 text-sm">
                  Rows Per Page:
                  <VSelect
                    v-model="options.itemsPerPage"
                    variant="plain"
                    class="per-page-select text-high-emphasis"
                    density="compact"
                    :items="[10, 20, 25, 50, 100]"
                    @update:modelValue="($event: number) => {
                      updateCacheMemory('DOCKET', 'ITEM_PER_PAGE', $event);
                      fetchDockets()
                    }
                  "
                  />
                </div>
                <div class="d-flex text-sm align-center text-high-emphasis" />
                <div class="d-flex gap-x-2 align-center">
                  <!-- Previous page -->
                  <VBtn
                    class="flip-in-rtl"
                    icon="mdi-chevron-left"
                    variant="text"
                    density="comfortable"
                    color="default"
                    :disabled="options.page <= 1"
                    @click="
                      () => {
                        options.page <= 1 ? (options.page = 1) : options.page--,
                          fetchDockets();
                      }
                    "
                  />

                  <!-- Next page -->
                  <VBtn
                    class="flip-in-rtl"
                    icon="mdi-chevron-right"
                    density="comfortable"
                    variant="text"
                    color="default"
                    :disabled="
                      options.page >=
                      Math.ceil(totalDockets / options.itemsPerPage)
                    "
                    @click="
                      () => {
                        options.page >=
                        Math.ceil(totalDockets / options.itemsPerPage)
                          ? (options.page = Math.ceil(
                              totalDockets / options.itemsPerPage
                            ))
                          : options.page++,
                          fetchDockets();
                      }
                    "
                  />
                </div>
              </div>
            </template>
          </VDataTableServer>
        </VCardText>

        <!-- Dialog actions -->
        <VCardActions>
          <VSpacer />

          <!-- Add to my tab -->
          <VBtn
            color="success"
            variant="flat"
            :disabled="!selectedDocket"
            @click.prevent="emit('addToMyTab', selectedDocket)"
          >
            ADD
          </VBtn>

          <!-- Close -->
          <VBtn
            color="error"
            variant="flat"
            @click="emit('closeAddToMyTabDialog', false)"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
